import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Download, Search, X } from "lucide-react";

const STATUSES = [
  ["single", "Single"],
  ["married", "Married"],
  ["head", "Head of Household"],
];

const pct = (r) => `${(Number(r) * 100).toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}%`;
const usd = (n) => {
  const v = Number(n || 0);
  // CPA-facing figures always show cents when they exist (168.30, not 168.3).
  const cents = Math.round(v * 100) % 100 !== 0;
  return `$${v.toLocaleString("en-US", {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};
const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const ceiling = (v) => (v === null || v === undefined ? "and above" : `up to ${usd(v)}`);

const SourceBadge = ({ source }) => {
  const map = {
    official: "bg-ink text-white",
    provisional: "bg-yellow-300 text-ink",
    indexed_estimate: "bg-yellow-300 text-ink",
  };
  return (
    <span className={`border-2 border-ink px-2 py-0.5 font-mono text-[10px] uppercase ${map[source] || "bg-white"}`}>
      {String(source || "unknown").replace("_", " ")}
    </span>
  );
};

const BracketTable = ({ rows }) => (
  <table className="w-full font-mono text-xs border-2 border-ink">
    <thead>
      <tr className="bg-secondary/60 border-b-2 border-ink">
        <th className="text-left p-2">Annual taxable wages</th>
        {rows.some((r) => r.base_tax !== undefined) && <th className="text-right p-2">Base tax</th>}
        <th className="text-right p-2">Marginal rate</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-ink/20">
          <td className="p-2">
            {r.over !== undefined
              ? (r.up_to === null ? `over ${usd(r.over)}` : `${usd(r.over)} – ${usd(r.up_to)}`)
              : ceiling(r.up_to)}
          </td>
          {r.base_tax !== undefined && <td className="p-2 text-right">{usd(r.base_tax)}</td>}
          <td className="p-2 text-right font-bold">{pct(r.rate)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function TaxTables() {
  const [year, setYear] = useState(2026);
  const [tab, setTab] = useState("states");
  const [data, setData] = useState(null);
  const [fed, setFed] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("single");

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        api.get(`/tax/state-tables?year=${year}`),
        api.get(`/tax/federal-tables?year=${year}`),
      ]);
      setData(s.data);
      setFed(f.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const open = async (st) => {
    setDetail({ loading: true, state: st });
    try {
      const { data: d } = await api.get(`/tax/state-tables/${st}?year=${year}`);
      setDetail((cur) => (cur && cur.state === st ? d : cur));
    } catch (e) {
      setDetail((cur) => (cur && cur.state === st ? null : cur));
      toast.error(formatApiError(e));
    }
  };

  const rows = useMemo(() => {
    const all = data?.states || [];
    const needle = q.trim().toUpperCase();
    const filtered = needle ? all.filter((r) => r.state.includes(needle)) : all;
    return [...filtered].sort((a, b) => (b.in_nexus ? 1 : 0) - (a.in_nexus ? 1 : 0) || a.state.localeCompare(b.state));
  }, [data, q]);

  const exportCsv = () => {
    const head = ["jurisdiction", "in_nexus", "levies_income_tax", "brackets", "top_rate",
                  "supplemental_rate", "std_deduction_single", "exemption_credit", "source"];
    const lines = [head.join(",")].concat((data?.states || []).map((r) => [
      r.state, r.in_nexus, r.levies_income_tax, r.bracket_count, r.top_rate,
      r.supplemental_rate, r.standard_deduction?.single ?? "", r.exemption_credit, r.source,
    ].map(csvCell).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `checkmate-tax-tables-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="tax-tables-page">
      <div className="border-2 border-ink p-6 bg-secondary/40">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-3">
              <Landmark className="w-7 h-7" /> TAX TABLES
            </h1>
            <p className="text-sm mt-2 max-w-2xl">
              The exact federal and state figures this engine used to compute your payroll.
              Every jurisdiction carries its source flag, so a CPA can see what is published
              law and what is carried forward.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[2026, 2027].map((y) => (
              <button key={y} onClick={() => setYear(y)} data-testid={`tax-year-${y}`}
                className={`border-2 border-ink px-3 py-2 font-mono text-xs ${year === y ? "bg-ink text-white" : "bg-white hover:bg-secondary"}`}>
                {y}
              </button>
            ))}
            <button onClick={exportCsv} data-testid="tax-export-csv"
              className="border-2 border-ink px-3 py-2 font-mono text-xs uppercase bg-white hover:bg-ink hover:text-white transition-colors flex items-center gap-2">
              <Download className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>
        {data && (
          <p className="font-mono text-xs mt-4" data-testid="tax-tables-meta">
            {data.jurisdiction_count} jurisdictions · nexus: {data.nexus_states?.length ? data.nexus_states.join(", ") : "none yet"}
            {data.provisional_year ? " · contains provisional figures" : " · all figures official"}
          </p>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["states", "State & Local"], ["federal", "Federal & FICA"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tax-tab-${k}`}
            className={`border-2 border-ink px-4 py-2 font-mono text-xs uppercase ${tab === k ? "bg-ink text-white" : "bg-white hover:bg-secondary"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "states" && (
        <>
          <div className="flex items-center gap-2 border-2 border-ink px-3 py-2 bg-white max-w-xs">
            <Search className="w-4 h-4" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter (e.g. NY)"
              data-testid="tax-state-search" className="font-mono text-xs w-full outline-none bg-transparent" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="tax-state-grid">
            {rows.map((r) => (
              <button key={r.state} onClick={() => open(r.state)} data-testid={`tax-state-${r.state}`}
                className="border-2 border-ink p-4 bg-white text-left hover:-translate-y-0.5 hover:shadow-[4px_4px_0_0_var(--ink,#000)] transition-transform">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-lg font-black">{r.state}</span>
                  <SourceBadge source={r.source} />
                </div>
                <p className="font-mono text-xs mt-2">
                  {r.levies_income_tax
                    ? `${r.bracket_count} bracket(s) · top ${pct(r.top_rate)}`
                    : "No wage income tax"}
                </p>
                <p className="font-mono text-[10px] mt-1 opacity-70">
                  {Object.keys(r.programs || {}).length
                    ? `programs: ${Object.values(r.programs).map((p) => p.label).join(", ")}`
                    : "no employee-side programs"}
                </p>
                {r.in_nexus && (
                  <span className="inline-block mt-2 border-2 border-ink bg-yellow-300 px-2 py-0.5 font-mono text-[10px] uppercase">
                    Your nexus
                  </span>
                )}
              </button>
            ))}
          </div>

          {!!data?.local_jurisdictions?.length && (
            <div className="border-2 border-ink p-5 bg-white" data-testid="tax-local-section">
              <h2 className="text-base md:text-lg font-black">MUNICIPAL / LOCAL JURISDICTIONS</h2>
              <p className="font-mono text-xs mt-1 opacity-70">
                Applied on top of the state calculation when the employee's worksite or residency matches.
              </p>
              <div className="overflow-x-auto mt-4">
                <table className="w-full font-mono text-xs border-2 border-ink min-w-[420px]">
                  <thead>
                    <tr className="bg-secondary/60 border-b-2 border-ink">
                      <th className="text-left p-2">Jurisdiction</th>
                      <th className="text-left p-2">State</th>
                      <th className="text-left p-2">Basis</th>
                      <th className="text-right p-2">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.local_jurisdictions.map((l) => (
                      <tr key={l.key} className="border-b border-ink/20" data-testid={`tax-local-${l.key}`}>
                        <td className="p-2">{l.label}</td>
                        <td className="p-2">{l.state}</td>
                        <td className="p-2">{l.type === "state_surcharge" ? "% of state tax" : "% of wages"}</td>
                        <td className="p-2 text-right font-bold">{pct(l.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "federal" && fed && (
        <div className="space-y-6" data-testid="tax-federal-panel">
          <div className="border-2 border-ink p-5 bg-white">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-base md:text-lg font-black">FEDERAL WITHHOLDING</h2>
              <SourceBadge source="official" />
            </div>
            <p className="font-mono text-xs mt-2">{fed.source}</p>
            {fed.held_forward && (
              <p className="font-mono text-xs mt-1">
                {fed.year} tables are not published yet — held at the {fed.effective_table_year} schedule.
              </p>
            )}
            <div className="flex gap-2 mt-4 flex-wrap">
              {STATUSES.map(([k, label]) => (
                <button key={k} onClick={() => setStatus(k)} data-testid={`tax-fed-status-${k}`}
                  className={`border-2 border-ink px-3 py-1 font-mono text-xs ${status === k ? "bg-ink text-white" : "bg-white hover:bg-secondary"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="grid gap-6 lg:grid-cols-2 mt-4">
              <div>
                <h3 className="font-mono text-xs uppercase mb-2">Standard schedule</h3>
                <BracketTable rows={fed.standard[status] || []} />
              </div>
              <div>
                <h3 className="font-mono text-xs uppercase mb-2">W-4 Step 2 checked (higher)</h3>
                <BracketTable rows={fed.step2_checked[status] || []} />
              </div>
            </div>
          </div>

          <div className="border-2 border-ink p-5 bg-white" data-testid="tax-fica-panel">
            <h2 className="text-base md:text-lg font-black">FICA</h2>
            <ul className="font-mono text-xs mt-3 space-y-1">
              <li>Social Security · {pct(fed.fica.social_security_rate)} up to {usd(fed.fica.ss_wage_base)} of SS/Medicare-taxable wages</li>
              <li>Medicare · {pct(fed.fica.medicare_rate)} — uncapped</li>
              <li>Wage base by year · {Object.entries(fed.fica.ss_wage_base_by_year).map(([y, v]) => `${y}: ${usd(v)}`).join(" · ")}</li>
              <li>Withholding stops mid-period the moment YTD wages cross the cap; only the sub-cap portion is taxed.</li>
            </ul>
          </div>
        </div>
      )}

      {detail && (
        // !mt-0 — this overlay is a direct child of a `space-y-8` container,
        // which would otherwise push the fixed backdrop down 2rem and leave the
        // header strip clickable behind it.
        <div className="fixed inset-0 !mt-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="tax-detail-modal">
          <div className="bg-white border-2 border-ink max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between border-b-2 border-ink p-4 gap-3">
              <h3 className="font-black text-base md:text-lg flex items-center gap-3">
                {detail.state} · {year} {detail.source && <SourceBadge source={detail.source} />}
              </h3>
              <button onClick={() => setDetail(null)} data-testid="tax-detail-close"
                className="border-2 border-ink px-3 py-1 font-mono text-xs uppercase flex items-center gap-1">
                <X className="w-3 h-3" /> Close
              </button>
            </div>
            <div className="p-4 overflow-auto space-y-5" data-testid="tax-detail-body">
              {detail.loading && <p className="font-mono text-xs">LOADING…</p>}
              {!detail.loading && !detail.levies_income_tax && (
                <p className="font-mono text-xs">This jurisdiction levies no state income tax on wages.</p>
              )}
              {!detail.loading && detail.levies_income_tax && (
                <>
                  <div className="flex gap-2 flex-wrap">
                    {STATUSES.filter(([k]) => detail.brackets?.[k]).map(([k, label]) => (
                      <button key={k} onClick={() => setStatus(k)} data-testid={`tax-detail-status-${k}`}
                        className={`border-2 border-ink px-3 py-1 font-mono text-xs ${status === k ? "bg-ink text-white" : "bg-white hover:bg-secondary"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <BracketTable rows={detail.brackets?.[status] || detail.brackets?.single || []} />
                  <ul className="font-mono text-xs space-y-1">
                    <li>Standard deduction · {Object.entries(detail.standard_deduction || {}).map(([k, v]) => `${k}: ${usd(v)}`).join(" · ") || "none"}</li>
                    <li>Exemption credit · {usd(detail.exemption_credit)} per allowance / year</li>
                    {!!detail.exemption_deduction && (
                      <li>Exemption deduction · {usd(detail.exemption_deduction)} per allowance / year</li>
                    )}
                    {!!Object.keys(detail.low_income_exemption || {}).length && (
                      <li>Low-income exemption (withholding drops to zero below) · {Object.entries(detail.low_income_exemption).map(([k, v]) => `${k}: ${usd(v)}`).join(" · ")}</li>
                    )}
                    <li>Supplemental / bonus rate · {pct(detail.supplemental_rate)}</li>
                  </ul>
                </>
              )}
              {!detail.loading && !!Object.keys(detail.programs || {}).length && (
                <div>
                  <h4 className="font-mono text-xs uppercase mb-2">Statutory programs (employee side)</h4>
                  <table className="w-full font-mono text-xs border-2 border-ink">
                    <tbody>
                      {Object.entries(detail.programs).map(([k, p]) => (
                        <tr key={k} className="border-b border-ink/20">
                          <td className="p-2">{p.label}</td>
                          <td className="p-2 text-right">{pct(p.rate)}</td>
                          <td className="p-2 text-right">{p.wage_base ? `wage base ${usd(p.wage_base)}` : "uncapped"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!detail.loading && !!detail.locals?.length && (
                <div>
                  <h4 className="font-mono text-xs uppercase mb-2">Local jurisdictions</h4>
                  <ul className="font-mono text-xs space-y-1">
                    {detail.locals.map((l) => (
                      <li key={l.key}>{l.label} · {pct(l.rate)} {l.type === "state_surcharge" ? "of state tax" : "of wages"}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
